import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {AIMode} from '@taopix/taopix-core';
import {useUploader} from '../../Context/Upload/UploadContext';
import DeviceUploaderStatusPanel from './DeviceUploaderStatusPanel';
import {AssetService} from '@taopix/taopix-asset-manager/dist/types/UploadServiceProvider';
import useSettings from '../../../Hooks/useSettings';
import {Alert} from '@taopix/taopix-design-system';
import {useTranslation} from 'react-i18next';

export type DeviceUploaderProps = {
  expired: boolean;
};

const DeviceUploader = ({expired}: DeviceUploaderProps) => {
  const uploader = useUploader();
  const settings = useSettings();
  const {t} = useTranslation();
  const [total, setTotal] = useState(0);
  const [completed, setCompleted] = useState(0);
  const [isCancelled, setIsCancelled] = useState(false);
  const [error, setError] = useState({
    visible: false,
    message: '',
  });

  // memo the uploader
  const processor = useMemo(() => {
    if (!uploader.hasOwnProperty('get')) {
      return null;
    }

    // default the asset service to upload from device
    const p = uploader.get({
      code: '%UPLOADFROMDEVICE%',
    } as unknown as AssetService);

    // turn off AI mode for assets uploaded from phone
    p.setOption('aiMode', AIMode.NotAvailable);

    // when an asset is queued, increment the counter
    p.on('queued', event => {
      // if the cancel has been called, then we don't want to count
      if (!isCancelled) {
        // use an updater function here to ensure we have the correct count
        setCompleted(current => current + 1);
      }
    });

    p.on('chunk', event => {
      const {chunk} = event.detail;
      setTotal(current => current + chunk.length);
    });

    return p;
  }, [uploader, isCancelled]);

  useEffect(() => {
    if (expired) {
      onCancelUpload();
    }
  }, [expired]);

  // Open the OS file browser
  const onUploadFiles = useCallback(() => {
    if (!processor) {
      return null;
    }

    if (
      0 ===
      settings.state.imageServers.servers.map(server => server.active).filter(truthy => truthy)
        .length
    ) {
      return setError({
        visible: true,
        message: '*:str_MessageNoActiveImageServers',
      });
    }

    // open the device file browser
    processor.open().then(payload => {
      setIsCancelled(current => false);
      //set the total number of files read
      // setTotal(current => current + payload.assets.length);
    });
  }, [processor]);

  // Cancel the upload
  const onCancelUpload = useCallback(() => {
    if (!processor) {
      return null;
    }
    processor.abort();
    setCompleted(0);
    setTotal(0);
    setIsCancelled(true);
  }, [processor]);

  return (
    <>
      <DeviceUploaderStatusPanel
        total={total}
        completed={completed}
        onTriggerUpload={onUploadFiles}
        onCancel={onCancelUpload}
        expired={expired}
      />
      <Alert
        id={'asset-service-list'}
        open={error.visible}
        type={'info'}
        message={t(error.message)}
        onClose={() =>
          setError(current => {
            return {
              ...current,
              visible: false,
            };
          })
        }
      />
    </>
  );
};

export default DeviceUploader;
